import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import {
  IconAdjustmentsHorizontal,
  IconAlertTriangle,
  IconClock,
  IconEye,
  IconLoader2,
  IconX,
} from "@tabler/icons-react";
import { useToast } from "@/components/Toast/Toast";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { OptionInput } from "@/components/Inputs/OptionInput";
import { RolePreviewCapabilityEditor } from "@/components/PBanner/RolePreviewCapabilityEditor";
import {
  hasScopeEdits,
  isRemintOwed,
  usePreviewStore,
  type PreviewTarget,
} from "@/lib/preview/previewStore";
import { IMPLICIT_CAPS } from "@/lib/authz/capabilities";
import { DUR, EASE } from "@/lib/ui/motion";
import type { Capability } from "@/types/Capability";
import { fmtCountdown } from "@/lib/preview/fmtCountdown";
import {
  previewStartErrorText,
  translateErrorCode,
  type ErrorsTranslator,
  type RolePreviewTranslator,
} from "@/lib/preview/previewErrors";

/** Base roles offered in the switcher (OWNER omitted — same surface as ADMIN). */
const BASE_ROLES = ["VIEWER", "EDITOR", "ADMIN"] as const;

/**
 * Sticky banner shown while "preview as role" is active. Surfaces the role
 * being previewed once — as the role switcher itself (no duplicate chip), with
 * a role-colored dot beside it — plus a single read-only lock (no re-fetch on
 * switch: uses the targets captured at start plus client-side base roles), an
 * expiry countdown, and an exit button. Visual: a clean full-width
 * `surface-primary` bar with a `border-b outline-quaternary` separator — reads
 * as a second toolbar row flush above the appbar — with a role-colored accent;
 * slides down on mount.
 *
 * Read-only is enforced server-side (the preview token carries no write caps),
 * so write actions simply 403 like they would for a real read-only role — the
 * banner just communicates that the session is read-only.
 *
 * Placement: rendered ABOVE the page-root scroller in EventLayoutShell (not
 * inside it) so it never collides with the sticky appbar.
 */
export function RolePreviewBanner({
  /** The ADMIN's real capability to persist edits back to a template — resolved
   *  by the shell from the logged-in role (NOT the previewed one). */
  canSaveToTemplate = false,
}: {
  canSaveToTemplate?: boolean;
} = {}) {
  const t = useTranslations("rolePreview");
  const tErrors = useTranslations("errors");
  const { showToast } = useToast();
  const active = usePreviewStore((s) => s.active);
  const eventId = usePreviewStore((s) => s.eventId);
  const target = usePreviewStore((s) => s.target);
  const expiresAt = usePreviewStore((s) => s.expiresAt);
  const availableTargets = usePreviewStore((s) => s.availableTargets);
  const startPreview = usePreviewStore((s) => s.startPreview);
  const stopPreview = usePreviewStore((s) => s.stopPreview);
  const baseCaps = usePreviewStore((s) => s.baseCaps);
  const effectiveCaps = usePreviewStore((s) => s.effectiveCaps);
  const reminting = usePreviewStore((s) => s.reminting);
  // B2-0 (M5, LOW-13): re-mint states the CLOSED editor would otherwise hide.
  const remintError = usePreviewStore((s) => s.remintError);
  const remintErrorCode = usePreviewStore((s) => s.remintErrorCode);
  const remintPausedUntil = usePreviewStore((s) => s.remintPausedUntil);
  const remintOwed = usePreviewStore(isRemintOwed);
  // Audit r3 (store LOW-2): a forced re-mint owed — the token may carry the
  // edited state, and still the pause end must re-mint it.
  const remintDebt = usePreviewStore((s) => s.remintDebt);
  const scopeNotApplied = usePreviewStore((s) => s.scopeUnsupported && hasScopeEdits(s));

  const [now, setNow] = useState(() => Date.now());
  const [editorOpen, setEditorOpen] = useState(false);

  // "Edited" indicator: effectiveCaps diverged from the role's real caps.
  const capsModified = useMemo(() => {
    if (baseCaps.length !== effectiveCaps.length) return true;
    const set = new Set(baseCaps);
    return !effectiveCaps.every((c) => set.has(c));
  }, [baseCaps, effectiveCaps]);

  // 1 Hz countdown tick.
  useEffect(() => {
    if (!active || expiresAt == null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active, expiresAt]);

  const baseTargets: PreviewTarget[] = useMemo(
    () =>
      BASE_ROLES.map((role) => ({
        kind: "baseRole" as const,
        baseRole: role,
        name: t(`baseRole.${role}`),
        capabilities: Array.from(IMPLICIT_CAPS[role] ?? []) as Capability[],
        fieldAllowlist: null,
        rowFilters: [],
      })),
    [t],
  );

  const currentValue = target
    ? target.kind === "template"
      ? `template:${target.templateId}`
      : `base:${target.baseRole}`
    : "";

  // Switcher options: captured templates, then base roles, plus the current
  // target if it isn't already listed (e.g. previewed from a collaborator card
  // with no captured list) so the control always shows the right label.
  const options = useMemo(() => {
    const out: { value: string; label: string; tgt: PreviewTarget }[] = [];
    for (const tgt of availableTargets) {
      out.push({ value: `template:${tgt.templateId}`, label: tgt.name, tgt });
    }
    for (const tgt of baseTargets) {
      out.push({ value: `base:${tgt.baseRole}`, label: tgt.name, tgt });
    }
    if (target && !out.some((o) => o.value === currentValue)) {
      out.unshift({ value: currentValue, label: target.name, tgt: target });
    }
    return out;
  }, [availableTargets, baseTargets, target, currentValue]);

  const optionValues = useMemo(() => options.map((o) => o.value), [options]);
  const optionLabels = useMemo(
    () => Object.fromEntries(options.map((o) => [o.value, o.label])),
    [options],
  );

  if (!active || !target) return null;

  const onSwitch = async (value: string) => {
    if (value === currentValue || !eventId) return;
    const chosen = options.find((o) => o.value === value)?.tgt;
    if (!chosen) return;
    try {
      await startPreview(eventId, chosen);
    } catch (e) {
      // A 429 names the wait in its own words (LOW-15): a switch with a live
      // predecessor draws on the re-mint bucket the edits drain.
      showToast(previewStartErrorText(e, t as unknown as RolePreviewTranslator, "switch"), "error");
    }
  };

  const msLeft = expiresAt != null ? expiresAt - now : null;
  const urgent = msLeft != null && msLeft <= 60_000;

  // The edit button's warning badge, in place of the orange "edited" dot:
  // paused (429, an edit or a forced re-mint owed) > error (failed or refused re-mint) >
  // unsupported (the server did not apply an override, a scope edit is open).
  // Audit r1 F2: the paused NAME is one static sentence — the banner is a
  // live region, and an aria-label/title with the m:ss changed every second;
  // the m:ss stays in the aria-hidden badge. Audit r2 (store LOW-2): a pause
  // that outlasts the session (the editor footer's own test) says the data
  // stays until the end, like the footer — never that it updates after the wait.
  const pausedUntilEnd =
    remintPausedUntil != null && expiresAt != null && remintPausedUntil >= expiresAt - 15_000;
  const badge: { kind: "paused" | "error" | "unsupported"; state: string } | null =
    remintPausedUntil != null && (remintOwed || remintDebt)
      ? {
          kind: "paused",
          state: pausedUntilEnd ? t("remintRateLimitedUntilEnd") : t("remintRateLimitedStatus"),
        }
      : remintError || remintErrorCode
        ? {
            kind: "error",
            state: remintErrorCode
              ? (translateErrorCode(remintErrorCode, tErrors as unknown as ErrorsTranslator) ??
                t("remintRejected"))
              : t("remintError"),
          }
        : scopeNotApplied
          ? { kind: "unsupported", state: t("scopeNotApplied") }
          : null;
  const editLabel = badge ? `${t("editRole")} – ${badge.state}` : t("editRole");
  // Audit r3 (store LOW-4): the m:ss only for a pause that ends inside the
  // session — one that outlasts it counted on past the session's end beside
  // the banner's own countdown; it shows the warning triangle instead.
  const pausedCountdown = badge?.kind === "paused" && !pausedUntilEnd;

  // Thin vertical rule separating the left-cluster groups.
  const Divider = () => (
    <span aria-hidden className="shrink-0 h-5 w-px border-l outline-quaternary" />
  );

  return (
    <motion.div
      role="status"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: DUR.base, ease: EASE.standard }}
      className="surface-primary font-primary border-b outline-quaternary px-4 py-2.5 flex items-center justify-between gap-4"
    >
      {/* Left: three grouped clusters separated by rules —
          (A) eye + switcher, (B) edit-role action. */}
      <div className="flex items-center gap-3 min-w-0">
        {/* A — eye + switcher (eye in a neutral tone, no label/dot). */}
        <div className="flex items-center gap-2 min-w-0">
          <IconEye size={16} className="shrink-0 font-secondary" />
          <OptionInput
            options={optionValues}
            optionLabels={optionLabels}
            defaultValue={currentValue}
            setParent={onSwitch}
            required={false}
            strictOption
            width="w-44"
            height="h-8"
            fontSize="body-3-regular"
          />
        </div>

        <Divider />

        {/* B — edit role (icon-only ghost; orange "edited" badge when modified). */}
        <button
          type="button"
          onClick={() => setEditorOpen(true)}
          title={editLabel}
          aria-label={editLabel}
          className={`relative shrink-0 flex items-center justify-center h-8 rounded-md hover:surface-secondary transition-colors ${
            pausedCountdown ? "min-w-8 px-1.5 gap-1" : "w-8"
          } ${
            badge ? "font-status-amber-dark" : capsModified ? "chip-orange-fg" : "font-tertiary"
          }`}
        >
          {reminting ? (
            <IconLoader2 size={16} className="animate-spin" />
          ) : (
            <IconAdjustmentsHorizontal size={16} />
          )}
          {badge ? (
            pausedCountdown ? (
              <span
                data-testid="role-preview-edit-badge"
                data-kind="paused"
                aria-hidden
                className="body-4-medium tabular-nums"
              >
                {fmtCountdown((remintPausedUntil ?? now) - now)}
              </span>
            ) : (
              <span
                data-testid="role-preview-edit-badge"
                data-kind={badge.kind}
                aria-hidden
                className="absolute top-0.5 right-0.5"
              >
                <IconAlertTriangle size={10} />
              </span>
            )
          ) : (
            capsModified && (
              <span
                className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full chip-orange-bg"
                aria-hidden
              />
            )
          )}
        </button>
      </div>

      {/* Right: labeled countdown + exit (the primary escape hatch). */}
      <div className="flex items-center gap-3 shrink-0">
        {msLeft != null && (
          <span
            title={t("autoEndHint")}
            aria-live={urgent ? "polite" : "off"}
            className={`shrink-0 flex items-center gap-1 body-3-regular tabular-nums transition-colors ${
              urgent ? "chip-red-fg animate-pulse" : "font-tertiary"
            }`}
          >
            <IconClock size={14} />
            <span className="hidden sm:inline">{t("endsIn")}&nbsp;</span>
            {fmtCountdown(msLeft)}
          </span>
        )}
        <Divider />
        <SecondaryButton
          text={t("exit")}
          icon={IconX}
          iconSize={16}
          size="xsmall"
          width="w-max"
          height="h-8"
          color="surface-secondary"
          borderColor="outline-secondary"
          ariaLabel={t("exit")}
          onClick={() => void stopPreview()}
        />
      </div>

      {editorOpen && (
        <RolePreviewCapabilityEditor
          canSaveToTemplate={canSaveToTemplate}
          onClose={() => setEditorOpen(false)}
          now={now}
        />
      )}
    </motion.div>
  );
}
